import { useCallback, useMemo } from 'react'
import type React from 'react'

export type ContextTarget = {
    id: string
    label?: string
    extra?: any
}

/**
 * RSuite Tree context menu hook.
 * - Pass the current treeNodes (the same array you give <Tree data={...}>)
 * - Pass a show(event, target) function that opens your context menu.
 */
export function useContextMenuTree(
    treeNodes: any[] | undefined,
    show: (event: React.MouseEvent, target: ContextTarget) => void
) {
    // Build value -> node map; recomputed whenever treeNodes changes
    const nodeByValue = useMemo(() => {
        const map = new Map<string, any>()
        const walk = (nodes?: any[]) =>
            nodes?.forEach((n) => {
                if (!n) return
                const hasChildren = Array.isArray(n.children) && n.children.length > 0
                if (!hasChildren) {
                    map.set(String(n.value), n)
                } else {
                    walk(n.children)
                }
            })
        walk(treeNodes)
        return map
    }, [treeNodes])

    const handler = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (event.button !== 2) return

            const domTarget = event.target as HTMLElement | null
            if (!domTarget) return

            const container = domTarget.closest('.rs-tree-node') as HTMLElement | null
            if (!container) return

            const value = container.getAttribute('value')
            if (!value) return

            const node = nodeByValue.get(value)
            if (!node) return

            const target: ContextTarget = {
                id: node.id,
                label: node.label,
                extra: node,
            }

            event.preventDefault()
            event.stopPropagation()
            show(event, target)
        },
        [nodeByValue, show]
    )

    return handler
}

/**
 * RSuite Table context menu hook.
 *
 * Usage (with <Table>):
 *   const handleRowContextMenu = useContextMenuTable(
 *     showContextMenu,
 *     (row: TUser) => ({ id: row._id, label: rowDisplayName(row), extra: row })
 *   )
 *
 *   <Table
 *     ...
 *     onRowContextMenu={handleRowContextMenu}
 *   />
 *
 * - `show` is the same "bridge" you use for tree: (event, target) => void
 * - `getTarget` maps a row object to { id, label, extra }
 */
export function useContextMenuTable<T>(
    show: (event: React.MouseEvent, target: ContextTarget) => void,
    getTarget: (rowData: T) => ContextTarget
) {
    const handler = useCallback(
        (rowData: T, event: React.MouseEvent) => {
            if (event.button !== 2) return

            event.preventDefault()
            event.stopPropagation()

            const target = getTarget(rowData)
            show(event, target)
        },
        [show, getTarget]
    )

    return handler
}
